/**
 * Utility functions for handling API errors across the application
 */

/**
 * Parse and format API error responses for auth endpoints
 * @param {Response} response - The fetch response object
 * @param {Object} result - The parsed JSON response
 * @param {Object} customMessages - Optional custom error messages for specific status codes
 * @returns {string} - Formatted error message
 */
export const parseAuthError = (response, result, customMessages = {}) => {
  // Check for custom messages first
  if (customMessages[response.status]) {
    return customMessages[response.status];
  }

  switch (response.status) {
    case 400:
      // Handle validation errors or business logic errors
      if (result.detail && typeof result.detail === 'string') {
        return result.detail;
      }
      if (result.detail && Array.isArray(result.detail)) {
        // Pydantic validation errors
        return result.detail.map(err => err.msg).join(', ');
      }
      return 'Invalid request. Please check your input.';

    case 401:
      return 'Invalid credentials. Please check your email and password.';

    case 403:
      return 'Access forbidden. You do not have permission to perform this action.';

    case 404:
      return 'The requested resource was not found.';

    case 409:
      return 'This resource already exists. Please use a different value.';

    case 422:
      // Validation errors
      if (result.detail && Array.isArray(result.detail)) {
        return result.detail.map(err => `${err.loc?.[1] || 'Field'}: ${err.msg}`).join(', ');
      }
      return result.detail || 'Please check your input format.';

    case 429:
      return 'Too many requests. Please wait a moment and try again.';

    case 500:
      return 'A server error occurred. Please try again later.';

    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';

    default:
      return result.detail || result.message || 'An unexpected error occurred.';
  }
};

/**
 * Handle network errors and other fetch-related errors
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const parseNetworkError = (error) => {
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return 'Network error. Please check your internet connection and try again.';
  }
  
  if (error.name === 'AbortError') {
    return 'Request timed out. Please try again.';
  }
  
  if (error.message.includes('Failed to fetch')) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  return error.message || 'An unexpected error occurred.';
};

/**
 * Wrapper function for API calls with error handling
 * @param {Function} apiCall - The API call function
 * @param {Object} customMessages - Optional custom error messages
 * @returns {Promise} - Promise that resolves to { success: boolean, data?: any, error?: string }
 */
export const withErrorHandling = async (apiCall, customMessages = {}) => {
  try {
    const response = await apiCall();
    
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      const errorMessage = parseAuthError(response, result, customMessages);
      return { success: false, error: errorMessage };
    }
    
    const data = await response.json().catch(() => ({}));
    return { success: true, data };
    
  } catch (error) {
    const errorMessage = parseNetworkError(error);
    return { success: false, error: errorMessage };
  }
};

/**
 * Common error messages for specific endpoints
 */
export const ERROR_MESSAGES = {
  register: {
    409: 'An account with this email already exists. Please try logging in instead.',
  },
  login: {
    401: 'Invalid email or password. Please check your credentials.',
  },
  verifyEmail: {
    404: 'Account not found. Please register first.',
    400: 'Invalid verification code. Please check the code and try again.',
  },
  resendVerification: {
    404: 'Account not found. Please register first.',
    400: 'Email is already verified. You can log in directly.',
  },
  updatePassword: {
    404: 'Account not found. Please log in again.',
    400: 'Current password is incorrect. Please try again.',
  },
  forgotPassword: {
    // Usually no specific errors for forgot password for security
  },
  resetPassword: {
    400: 'Invalid or expired reset token. Please request a new password reset.',
  },
};